import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Search, Plus, ChevronLeft, ChevronRight as ChevronRightIcon, 
  Edit, Trash2, Calendar, Check, X, ArrowUpRight, 
  Tag, RefreshCw, Sparkles, CheckCircle, ChevronDown, ChevronUp
} from "lucide-react";
import { useTranslation } from '../../contexts/LanguageContext';
import { useAuth } from "../../App"; // Import the auth hook

const SubscriptionList = ({ subscriptions, onAddSubscription }) => {
  const { t } = useTranslation();
  const { user, logout, validateToken, httpClient } = useAuth(); // Get auth context
  const navigate = useNavigate();
  
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedSubscription, setExpandedSubscription] = useState(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [subscriptionToDelete, setSubscriptionToDelete] = useState(null);
  const [editedSubscription, setEditedSubscription] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [availablePlans, setAvailablePlans] = useState(null);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [customPlanData, setCustomPlanData] = useState({
    name: '',
    price: '',
    interval: 'monthly'
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [expandedPlanDetails, setExpandedPlanDetails] = useState({});
  const [showCustomPlanForm, setShowCustomPlanForm] = useState(false);
  const itemsPerPage = 5;

  // Helper functions
  const formatDate = (dateString) => {
    if (!dateString) return t('common.na', 'N/A');
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const getBillingText = (interval, price, currency = 'EUR') => {
    const formattedPrice = price?.toFixed?.(2) || '0.00';
    const currencySymbol = currency === 'EUR' ? '€' : currency;
    
    switch (interval) {
      case 'monthly':
        return `${currencySymbol}${formattedPrice}/mo`;
      case 'annual':
        return `${currencySymbol}${formattedPrice}/yr`;
      case 'quarterly':
        return `${currencySymbol}${formattedPrice}/qtr`;
      default:
        return `${currencySymbol}${formattedPrice}`;
    }
  };

  // Filter and pagination
  const filteredSubscriptions = subscriptions.filter(sub => 
    sub.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sub.billing_interval?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sub.plan_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredSubscriptions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentSubscriptions = filteredSubscriptions.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleMerchantClick = (subscription) => {
    navigate(`/merchant/${subscription.merchant_id}`);
  };

  // Helper function to parse MongoDB Extended JSON
  const parseExtendedJSON = (data) => {
    const parseValue = (value) => {
      if (value && typeof value === 'object') {
        if (value.$oid) return value.$oid;
        if (value.$numberInt) return parseInt(value.$numberInt, 10);
        if (value.$numberDouble) return parseFloat(value.$numberDouble);
        if (value.$numberLong) return parseInt(value.$numberLong, 10);
        if (value.$date) return new Date(value.$date.$numberLong ? parseInt(value.$date.$numberLong, 10) : value.$date);
      }
      return value;
    };

    const deepParse = (obj) => {
      if (Array.isArray(obj)) {
        return obj.map(deepParse);
      } else if (obj && typeof obj === 'object') {
        const parsed = {};
        for (const key in obj) {
          parsed[key] = deepParse(parseValue(obj[key]));
        }
        return parsed;
      }
      return obj;
    };

    return deepParse(data);
  };

  // Fetch merchant plans when editing - UPDATED TO USE httpClient
  const fetchMerchantPlansAlternative = async (merchantId) => {
    try {
      setLoadingPlans(true);
      
      if (!httpClient || !merchantId) {
        console.error("HTTP client not available or merchant ID missing");
        return null;
      }
      
      const response = await httpClient.request(
        `/api/subscriptions/available-plans?merchant_id=${merchantId}`
      );
      
      if (!response.ok) {
        console.warn('Failed to fetch merchant plans:', response.status);
        return null;
      }
      
      const data = await response.json();
      
      if (data.success) {
        console.log('Available plans from API:', data);
        
        return {
          merchant: {
            name: data.merchant_name,
            logo: null,
            has_affiliate: data.has_affiliate || false
          },
          plans: data.available_plans || [],
          current_plan: data.current_plan
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching available plans:', error);
      return null;
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleEditClick = async (subscription, e) => {
    e.stopPropagation();
    
    // Check authentication
    if (!user) {
      navigate("/demo");
      return;
    }
    
    setEditedSubscription({...subscription});
    setExpandedSubscription(expandedSubscription === subscription.id ? null : subscription.id);
    setSaveSuccess(false);
    setAvailablePlans(null);
    setExpandedPlanDetails({});
    setShowCustomPlanForm(false);
    setCustomPlanData({
      name: '',
      price: '',
      interval: 'monthly'
    });
    
    // Fetch merchant plans
    if (subscription.merchant_id) {
      console.log('Fetching plans for merchant:', subscription.merchant_id);
      setLoadingPlans(true);
      try {
        const plansData = await fetchMerchantPlansAlternative(subscription.merchant_id);
        setAvailablePlans(plansData);
        
        if (!plansData || plansData.plans.length === 0) {
          console.warn('No plans found for merchant:', subscription.merchant_id);
        }
      } catch (error) {
        console.error('Failed to fetch plans:', error);
      } finally {
        setLoadingPlans(false);
      }
    }
  };

  const togglePlanDetails = (planId) => {
    setExpandedPlanDetails(prev => ({
      ...prev,
      [planId]: !prev[planId]
    }));
  };

  const handleSelectPlan = async (plan) => {
    try {
      setIsUpdating(true);
      
      if (!httpClient) {
        throw new Error("HTTP client not available");
      }

      const updates = {
        plan_details: {
          plan_id: plan.id,
          plan_name: plan.name,
          price: plan.price,
          billing_interval: plan.billing_interval,
          currency: plan.currency || 'EUR',
          is_custom: false
        }
      };

      const response = await httpClient.request('/api/update-subscription', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          merchant_id: editedSubscription.merchant_id,
          updates: updates
        }),
      });

      // Handle token expiration
      if (response.status === 401) {
        const revalidated = await validateToken();
        if (!revalidated) {
          navigate("/demo");
          return;
        }
        // Retry the request
        return handleSelectPlan(plan);
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || t('subscriptionList.updateFailed', 'Failed to update subscription'));
      }

      setSaveSuccess(true);
      setTimeout(() => {
        setExpandedSubscription(null);
        setSaveSuccess(false);
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error('Plan change failed:', error);
      alert(`${t('subscriptionList.updateFailed', 'Update failed')}: ${error.message}`);
      setIsUpdating(false);
    }
  };

  const handleCreateCustomPlan = () => {
    setShowCustomPlanForm(true);
  };

  const handleSaveCustomPlan = async () => {
    if (!customPlanData.name.trim()) {
      alert(t('subscriptionList.planNameRequired', 'Plan name is required'));
      return;
    }

    const price = parseFloat(customPlanData.price);
    if (isNaN(price) || price <= 0) {
      alert(t('subscriptionList.validPriceRequired', 'Please enter a valid price'));
      return;
    }

    try {
      setIsUpdating(true);
      
      if (!httpClient) {
        throw new Error("HTTP client not available");
      }

      const updates = {
        plan_details: {
          plan_id: `custom-${Date.now()}`,
          plan_name: customPlanData.name.trim(),
          price: price,
          billing_interval: customPlanData.interval,
          currency: editedSubscription.currency || 'EUR',
          is_custom: true
        }
      };

      const response = await httpClient.request('/api/update-subscription', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          merchant_id: editedSubscription.merchant_id,
          updates: updates
        }),
      });

      // Handle token expiration
      if (response.status === 401) {
        const revalidated = await validateToken();
        if (!revalidated) {
          navigate("/demo");
          return;
        }
        // Retry the request
        return handleSaveCustomPlan();
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || t('subscriptionList.updateFailed', 'Failed to create custom plan'));
      }

      setSaveSuccess(true);
      setTimeout(() => {
        setExpandedSubscription(null);
        setSaveSuccess(false);
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error('Custom plan creation failed:', error);
      alert(`${t('subscriptionList.updateFailed', 'Update failed')}: ${error.message}`);
      setIsUpdating(false);
    }
  };

  const handleUpdateSubscription = async () => {
    try {
      setIsUpdating(true);
      
      if (!httpClient) {
        throw new Error("HTTP client not available");
      }

      const updates = {
        next_billing_date: editedSubscription.next_billing_date
      };

      const response = await httpClient.request('/api/update-subscription', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          merchant_id: editedSubscription.merchant_id,
          updates: updates
        }),
      });

      // Handle token expiration
      if (response.status === 401) {
        const revalidated = await validateToken();
        if (!revalidated) {
          navigate("/demo");
          return;
        }
        // Retry the request
        return handleUpdateSubscription();
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || t('subscriptionList.updateFailed', 'Failed to update subscription'));
      }

      setSaveSuccess(true);
      setTimeout(() => {
        setExpandedSubscription(null);
        setSaveSuccess(false);
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error('Update failed:', error);
      alert(`${t('subscriptionList.updateFailed', 'Update failed')}: ${error.message}`);
      setIsUpdating(false);
    }
  };

  const handleDeleteClick = (subscription, e) => {
    e.stopPropagation();
    setSubscriptionToDelete(subscription);
    setShowConfirmDelete(true);
  };

  const confirmDelete = async () => {
    try {
      setIsUpdating(true);
      
      if (!httpClient) {
        throw new Error("HTTP client not available");
      }

      const response = await httpClient.request('/api/delete-subscription', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          merchant_id: subscriptionToDelete.merchant_id || subscriptionToDelete.id
        }),
      });

      // Handle token expiration
      if (response.status === 401) {
        const revalidated = await validateToken();
        if (!revalidated) {
          navigate("/demo");
          return;
        }
        // Retry the request
        return confirmDelete();
      }

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || t('subscriptionList.deleteFailed', 'Failed to delete subscription'));
      }

      setShowConfirmDelete(false);
      setExpandedSubscription(null);
      window.location.reload();
    } catch (error) {
      console.error('Delete failed:', error);
      alert(`${t('subscriptionList.deleteFailed', 'Deletion failed')}: ${error.message}`);
      setIsUpdating(false);
    }
  };

  // Render loading overlay EXACTLY like SubscriptionCollector
  const renderLoadingOverlay = () => (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] pointer-events-auto">
      <div className="bg-white p-8 rounded-xl shadow-xl max-w-md w-full mx-4 transform transition-all duration-300 scale-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#253985] mx-auto mb-6"></div>
          <h2 className="text-2xl font-bold mb-3 text-gray-800">
            {t('subscriptionList.updatingSubscription', 'Updating your subscription')}
          </h2>
          <p className="text-gray-600">
            {t('subscriptionList.pleaseWait', 'Please wait while we process your changes...')}
          </p>
          <div className="mt-4 h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
            <div className="animate-progress h-full bg-[#253985] rounded-full"></div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render unified edit view with both plan selection and billing date
  const renderEditView = () => (
    <div className="space-y-6">
      {/* Billing Date Section - Now First */}
      <div>
        <h3 className="text-lg font-semibold text-[#253985] mb-4">
          {t('subscriptionList.updateBillingDate', 'Update Billing Date')}
        </h3>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            {t('subscriptionList.nextBillingDate', 'Next Billing Date')}
          </label>
          <div className="relative">
            <Calendar className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
            <input
              type="date"
              value={editedSubscription?.next_billing_date?.split('T')[0] || ''}
              onChange={(e) => setEditedSubscription(prev => ({
                ...prev,
                next_billing_date: e.target.value
              }))}
              className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#253985] focus:border-[#253985] transition-all bg-white"
            />
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {t('subscriptionList.billingDateHelp', 'Update the next billing date for this subscription')}
          </p>
        </div>
      </div>

      {/* Plan Selection Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-[#253985]">
            {t('subscriptionList.changePlan', 'Change Plan')}
          </h3>
          {availablePlans?.plans && availablePlans.plans.length > 0 && (
            <span className="text-xs text-gray-500">
              {availablePlans.plans.length} {t('subscriptionList.plansAvailable', 'plans available')}
            </span>
          )}
        </div>
        
        {loadingPlans ? (
          <div className="flex flex-col items-center justify-center p-8">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#253985] mb-4"></div>
            <p className="text-gray-600">{t('subscriptionList.loadingPlans', 'Loading available plans...')}</p>
          </div>
        ) : availablePlans?.plans && availablePlans.plans.length > 0 ? (
          <div className="space-y-3">
            {availablePlans.plans.map((plan) => {
              const isExpanded = expandedPlanDetails[plan.id];
              const isCurrentPlan = editedSubscription.plan_id === plan.id;
              
              return (
                <div
                  key={plan.id || `plan-${Date.now()}-${Math.random()}`}
                  onClick={() => !isCurrentPlan && handleSelectPlan(plan)}
                  className={`border rounded-lg transition-all ${isCurrentPlan ? 'cursor-default' : 'cursor-pointer'} ${
                    isCurrentPlan
                      ? 'border-[#253985] bg-blue-50 ring-2 ring-blue-100'
                      : 'border-gray-200 hover:border-[#253985] hover:bg-blue-50/50'
                  }`}
                >
                  {/* Plan Header */}
                  <div className="flex items-center justify-between p-4">
                    <div className="flex items-center space-x-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        isCurrentPlan 
                          ? 'border-[#253985] bg-[#253985]' 
                          : 'border-gray-300'
                      }`}>
                        {isCurrentPlan && (
                          <div className="w-2 h-2 rounded-full bg-white"></div>
                        )}
                      </div>
                      <div>
                        <div className="flex items-center">
                          <h4 className="font-medium text-gray-800">
                            {plan.name}
                          </h4>
                          {isCurrentPlan && (
                            <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full flex items-center">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              {t('subscriptionList.current', 'Current')}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className="text-sm font-medium text-[#253985]">
                            {getBillingText(plan.billing_interval, plan.price, plan.currency)}
                          </span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                            {plan.billing_interval === 'monthly' ? t('common.monthly', 'Monthly') : t('common.annual', 'Annual')}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {plan.features && plan.features.length > 0 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            togglePlanDetails(plan.id);
                          }}
                          className="text-gray-400 hover:text-gray-600 p-1"
                        >
                          {isExpanded ? (
                            <ChevronUp className="w-5 h-5" />
                          ) : (
                            <ChevronDown className="w-5 h-5" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Expandable Details */}
                  {isExpanded && plan.features && plan.features.length > 0 && (
                    <div className="px-4 pb-4 border-t border-gray-200 pt-4">
                      <h5 className="text-sm font-medium text-gray-700 mb-2">
                        {t('subscriptionList.features', 'Features')}
                      </h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {plan.features.slice(0, 4).map((feature, idx) => (
                          <div key={idx} className="flex items-start space-x-2">
                            <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-gray-600">{feature}</span>
                          </div>
                        ))}
                        {plan.features.length > 4 && (
                          <div className="text-xs text-gray-500">
                            +{plan.features.length - 4} {t('subscriptionList.moreFeatures', 'more features')}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-yellow-800 text-sm">
              {t('subscriptionList.noPlansAvailable', 'No standard plans available for this merchant.')}
            </p>
          </div>
        )}
        
        {/* Custom Plan Option */}
        {!showCustomPlanForm && (
          <div
            onClick={handleCreateCustomPlan}
            className="p-6 border-2 border-dashed border-purple-300 rounded-lg cursor-pointer hover:border-purple-500 hover:bg-purple-50 transition-all group text-center"
          >
            <div className="inline-flex items-center justify-center w-12 h-12 bg-purple-100 rounded-full mb-3 group-hover:bg-purple-200 transition-colors">
              <Plus className="w-6 h-6 text-purple-600" />
            </div>
            <h4 className="font-medium text-purple-700">
              {t('subscriptionList.createCustomPlan', 'Create Custom Plan')}
            </h4>
            <p className="text-sm text-gray-600 mt-2 max-w-md mx-auto">
              {t('subscriptionList.createCustomDescription', "Can't find your plan? Create a custom one with your exact details.")}
            </p>
          </div>
        )}

        {/* Custom Plan Form */}
        {showCustomPlanForm && (
          <div className="space-y-6 p-4 border border-purple-200 rounded-lg bg-purple-50">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-purple-800">
                {t('subscriptionList.createCustomPlan', 'Create Custom Plan')}
              </h4>
              <button
                onClick={() => setShowCustomPlanForm(false)}
                className="text-purple-600 hover:text-purple-800 p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('subscriptionList.planName', 'Plan Name')}
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="text"
                  value={customPlanData.name}
                  onChange={(e) => setCustomPlanData(prev => ({
                    ...prev,
                    name: e.target.value
                  }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder={t('subscriptionList.enterPlanName', 'e.g., Premium Plus, Family Plan')}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('common.price', 'Price')}
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                      {editedSubscription.currency === 'EUR' ? '€' : editedSubscription.currency || '€'}
                    </span>
                    <input
                      type="number"
                      value={customPlanData.price}
                      onChange={(e) => setCustomPlanData(prev => ({
                        ...prev,
                        price: e.target.value
                      }))}
                      className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('subscriptionList.billingInterval', 'Billing Interval')}
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {['monthly', 'annual'].map((interval) => (
                      <button
                        key={interval}
                        type="button"
                        onClick={() => setCustomPlanData(prev => ({
                          ...prev,
                          interval
                        }))}
                        className={`py-3 text-center rounded-lg transition-all ${
                          customPlanData.interval === interval
                            ? 'bg-purple-600 text-white shadow-md'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {t(`common.${interval}`, interval.charAt(0).toUpperCase() + interval.slice(1))}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="bg-white border border-purple-200 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium text-gray-700">
                      {customPlanData.name || t('subscriptionList.yourCustomPlan', 'Your custom plan')}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {customPlanData.price 
                        ? `${editedSubscription.currency === 'EUR' ? '€' : editedSubscription.currency || '€'}${parseFloat(customPlanData.price || 0).toFixed(2)}/${customPlanData.interval === 'monthly' ? t('common.perMonthShort', 'mo') : t('common.perYearShort', 'yr')}`
                        : t('subscriptionList.priceNotSet', 'Price not set')
                      }
                    </p>
                  </div>
                  <span className="flex items-center text-xs bg-purple-100 text-purple-800 px-3 py-1 rounded-full">
                    <Tag className="w-3 h-3 mr-1" />
                    {t('subscriptionList.custom', 'Custom')}
                  </span>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowCustomPlanForm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  {t('common.cancel', 'Cancel')}
                </button>
                <button
                  onClick={handleSaveCustomPlan}
                  disabled={!customPlanData.name.trim() || !customPlanData.price}
                  className={`px-4 py-2 bg-purple-600 text-white rounded-lg flex items-center shadow-md hover:shadow-lg transition-all ${
                    (!customPlanData.name.trim() || !customPlanData.price) 
                      ? 'opacity-50 cursor-not-allowed' 
                      : 'hover:bg-purple-700'
                  }`}
                >
                  <Check className="h-4 w-4 mr-2" />
                  {t('subscriptionList.createPlan', 'Create Custom Plan')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between items-center pt-4 border-t border-gray-200">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setSubscriptionToDelete(editedSubscription);
            setShowConfirmDelete(true);
          }}
          className="flex items-center px-4 py-2 text-red-600 hover:text-red-800 transition-colors"
        >
          <Trash2 className="h-5 w-5 mr-2" />
          {t('subscriptionList.removeSubscription', 'Remove Subscription')}
        </button>
        
        <div className="flex items-center space-x-3">
          {saveSuccess && (
            <span className="text-green-600 text-sm flex items-center">
              <Check className="h-4 w-4 mr-1" /> 
              {t('subscriptionList.changesSaved', 'Changes saved!')}
            </span>
          )}
          <button
            onClick={() => setExpandedSubscription(null)}
            className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors flex items-center"
          >
            <X className="h-5 w-5 mr-2" />
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            onClick={handleUpdateSubscription}
            className="px-5 py-2.5 bg-gradient-to-r from-[#253985] to-[#1a2a6d] text-white rounded-lg hover:from-[#1a2a6d] hover:to-[#253985] transition-all flex items-center shadow-md hover:shadow-lg"
          >
            <Check className="h-5 w-5 mr-2" />
            {t('common.save', 'Save Changes')}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Show loading overlay when updating - MUST BE AT ROOT LEVEL */}
      {isUpdating && renderLoadingOverlay()}
      
      <div className="space-y-4 pb-8">
        {/* Search and Add Subscription Header */}
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between p-1">
          <div className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder={t('subscriptionList.searchPlaceholder', 'Search subscriptions...')}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#253985] focus:border-[#253985]"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
          
          <button
            onClick={onAddSubscription}
            className="flex items-center justify-center px-4 py-2 bg-gradient-to-r from-[#253985] to-[#1a2a6d] hover:from-[#1a2a6d] hover:to-[#253985] text-white rounded-lg shadow-md transition-all duration-300 hover:shadow-lg"
          >
            <Plus className="h-5 w-5 mr-2" />
            {t('subscriptionList.addSubscription', 'Add Subscription')}
          </button>
        </div>

        {/* Subscription Cards */}
        <div className="space-y-3 isolate">
          {currentSubscriptions.length > 0 ? (
            currentSubscriptions.map((subscription) => (
              <div key={subscription.id || subscription.name} className="group">
                {/* Main Subscription Card */}
                <div
                  className={`relative flex items-center justify-between p-4 rounded-xl transition-all duration-300 hover:shadow-md transform-gpu cursor-pointer ${
                    expandedSubscription === subscription.id 
                      ? "bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-[#253985]" 
                      : "bg-white border border-gray-200 hover:border-[#253985]/30"
                  }`}
                  onClick={() => handleMerchantClick(subscription)}
                >
                  <div className="absolute inset-0 rounded-xl -z-10" />
                  
                  <div className="relative flex items-center space-x-4 flex-1 min-w-0">
                    <div className="relative">
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-white flex items-center justify-center border border-gray-200 group-hover:border-[#253985]/50 transition-colors shadow-sm">
                        {subscription.logo ? (
                          <img
                            src={subscription.logo}
                            alt={subscription.name}
                            className="w-10 h-10 object-contain transition-transform duration-300 group-hover:scale-110"
                            onError={(e) => {
                              e.target.onerror = null; 
                              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI4IiBmaWxsPSJub25lIiBzdHJva2U9IiMyNTM5ODUiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj48cGF0aCBkPSJNMyA5bDktNyA5IDd2MTFhMiAyIDAgMCAxLTIgMkg1YTIgMiAwIDAgMS0yLTJ6Ij48L3BhdGg+PHBvbHlsaW5lIHBvaW50cz0iOSAyMiA5IDEyIDE1IDEyIDE1IDIyIj48L3BvbHlsaW5lPjwvc3ZnPg==';
                            }}
                          />
                        ) : (
                          <div className="text-[#253985]/50 group-hover:text-[#253985] transition-colors">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                              <polyline points="9 22 9 12 15 12 15 22"></polyline>
                            </svg>
                          </div>
                        )}
                      </div>
                      {subscription.is_custom && (
                        <div className="absolute -top-1 -right-1 bg-purple-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center shadow-sm">
                          <Tag className="w-3 h-3" />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center">
                        <h4 className="font-medium text-gray-800 group-hover:text-[#253985] transition-colors truncate">
                          {subscription.name}
                        </h4>
                        {subscription.is_custom && (
                          <span className="ml-2 text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full">
                            Custom
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 group-hover:text-[#253985]/80 transition-colors truncate">
                        {subscription.billing_interval ? 
                          `${subscription.billing_interval.charAt(0).toUpperCase()}${subscription.billing_interval.slice(1)} ${t('subscriptionList.plan', 'plan')}` : 
                          t('subscriptionList.monthlyPlan', 'Monthly plan')}
                      </p>
                    </div>
                  </div>

                  <div className="relative flex items-center space-x-6">
                    <div className="text-right">
                      <p className="font-medium text-gray-800 group-hover:text-[#253985] transition-colors">
                        {getBillingText(subscription.billing_interval, subscription.price, subscription.currency)}
                      </p>
                      <p className="text-xs text-gray-500 group-hover:text-[#253985]/70 transition-colors mt-1">
                        {t('subscriptionList.since', 'Since')} {formatDate(subscription.start_date)}
                      </p>
                    </div>

                    <div className="text-right min-w-[100px]">
                      <p className="text-sm text-gray-700 group-hover:text-[#253985] transition-colors">
                        {subscription.next_billing_date ? 
                          `${t('subscriptionList.next', 'Next')}: ${formatDate(subscription.next_billing_date)}` : 
                          t('subscriptionList.noDate', 'No date')}
                      </p>
                    </div>

                    <button 
                      onClick={(e) => handleEditClick(subscription, e)}
                      className="ml-2 p-2 rounded-lg text-gray-500 hover:text-white hover:bg-[#253985] transition-all duration-300 hover:scale-110"
                      aria-label={t('common.edit', 'Edit')}
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Expanded Edit Section */}
                {expandedSubscription === subscription.id && (
                  <div className="mt-1 p-6 bg-white border border-gray-200 rounded-lg shadow-lg transform transition-all duration-300">
                    {renderEditView()}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="text-center py-12 bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl border border-gray-200">
              <div className="mx-auto w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-inner mb-4">
                <ArrowUpRight className="h-10 w-10 text-[#253985]" />
              </div>
              <p className="text-gray-600 text-lg mb-2">
                {searchTerm ? 
                  t('subscriptionList.noSearchResults', 'No subscriptions match your search') : 
                  t('dashboard.noSubscriptions', 'No subscriptions found')}
              </p>
              <button
                onClick={onAddSubscription}
                className="mt-4 inline-flex items-center px-6 py-3 bg-gradient-to-r from-[#253985] to-[#1a2a6d] hover:from-[#1a2a6d] hover:to-[#253985] text-white rounded-lg shadow-md transition-all hover:shadow-lg"
              >
                <Plus className="h-6 w-6 mr-2" />
                {t('dashboard.addFirstSubscription', 'Add Your First Subscription')}
              </button>
            </div>
          )}
        </div>

        {/* Pagination Controls */}
        {filteredSubscriptions.length > itemsPerPage && (
          <div className="flex items-center justify-between mt-6 px-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`flex items-center px-4 py-2 rounded-lg transition-all ${
                currentPage === 1 
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                  : 'bg-[#253985] text-white hover:bg-[#1a2a6d] shadow-md hover:shadow-lg'
              }`}
            >
              <ChevronLeft className="h-5 w-5 mr-2" />
              {t('common.previous', 'Previous')}
            </button>
            
            <div className="flex items-center space-x-2">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all ${
                      currentPage === pageNum 
                        ? 'bg-white text-[#253985] border-2 border-[#253985] font-bold shadow-sm' 
                        : 'bg-[#253985] text-white hover:bg-[#1a2a6d] shadow-md hover:shadow-lg'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`flex items-center px-4 py-2 rounded-lg transition-all ${
                currentPage === totalPages 
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                  : 'bg-[#253985] text-white hover:bg-[#1a2a6d] shadow-md hover:shadow-lg'
              }`}
            >
              {t('common.next', 'Next')}
              <ChevronRightIcon className="h-5 w-5 ml-2" />
            </button>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showConfirmDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl transform transition-all duration-300">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-semibold text-gray-900">
                  {t('subscriptionList.confirmDeletion', 'Confirm Deletion')}
                </h3>
                <button
                  onClick={() => setShowConfirmDelete(false)}
                  className="text-gray-400 hover:text-gray-500 p-1"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="mb-6">
                <p className="text-gray-600">
                  {t('subscriptionList.confirmDeleteMessage', 'Are you sure you want to delete your')} <span className="font-medium text-[#253985]">{subscriptionToDelete?.name}</span> {t('subscriptionList.subscription', 'subscription')}?
                </p>
                <p className="text-gray-500 text-sm mt-2">
                  {t('subscriptionList.cannotUndo', 'This action cannot be undone.')}
                </p>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowConfirmDelete(false)}
                  className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  {t('common.cancel', 'Cancel')}
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-5 py-2.5 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-600 transition-all shadow-md hover:shadow-lg flex items-center"
                >
                  <Trash2 className="h-5 w-5 mr-2" />
                  {t('subscriptionList.deleteSubscription', 'Delete Subscription')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SubscriptionList;